'use client';

import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { Toaster, toast } from 'sonner';

import type { TechnicalStatus } from './configurable';

const FORM_TOASTER_ID = 'adipa-form-toast';

type ToastCopy = {
  title: string;
  message?: string;
};

type FormSubmissionToastOptions = {
  status: TechnicalStatus | 'submitting';
  success: ToastCopy;
  error: ToastCopy;
};

/** Mismo pop del acuse de los formularios: el envío se siente terminado. */
function ToastSuccessIcon() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.span
      className="flex"
      initial={shouldReduceMotion ? false : { scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 340, damping: 18 }}
    >
      <CircleCheck className="text-adipa-success h-5 w-5" aria-hidden="true" />
    </motion.span>
  );
}

export function FormToastViewport() {
  return (
    <Toaster
      id={FORM_TOASTER_ID}
      position="bottom-right"
      closeButton
      duration={4500}
      visibleToasts={4}
      icons={{
        success: <ToastSuccessIcon />,
        error: <CircleAlert className="text-adipa-error h-5 w-5" aria-hidden="true" />,
      }}
      toastOptions={{
        classNames: {
          toast: [
            'rounded-adipa-lg border bg-adipa-surface p-4 shadow-adipa-card',
            'text-adipa-text-primary',
          ].join(' '),
          title: 'text-sm font-semibold text-adipa-text-primary',
          description: 'text-sm leading-5 text-adipa-text-secondary',
          success: 'border-adipa-success-border',
          error: 'border-adipa-error-border',
          closeButton: 'border-adipa-border bg-adipa-surface text-adipa-text-secondary',
        },
      }}
    />
  );
}

export function useFormSubmissionToast({ error, status, success }: FormSubmissionToastOptions) {
  const previousStatusRef = useRef(status);

  useEffect(() => {
    if (previousStatusRef.current === status) return;
    previousStatusRef.current = status;

    if (status === 'success') {
      toast.success(success.title, {
        description: success.message,
        toasterId: FORM_TOASTER_ID,
      });
      return;
    }

    if (status === 'error') {
      toast.error(error.title, {
        description: error.message,
        toasterId: FORM_TOASTER_ID,
      });
    }
  }, [error.message, error.title, status, success.message, success.title]);
}
